import React, { useMemo, useEffect, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useTenant } from '@/contexts/TenantContext';
import { SINGLE_TENANT } from '@/config/singleTenant';
import { IITH } from '@/pages/landing/iithBrand';
import { detectRole, ROLE_CHROME, HOSPITAL_PORTAL_CHROME } from '@/modules/login/loginRoleChrome';
import { useLoginAuth } from '@/modules/login/hooks/useLoginAuth';
import LoginBrandPanel from '@/modules/login/components/LoginBrandPanel';
import LoginFormPanel from '@/modules/login/components/LoginFormPanel';
import LoginTenantGate from '@/modules/login/components/LoginTenantGate';

export default function LoginPage() {
  const { setUser } = useAuth();
  const { slug, setSlug, availableTenants, tenant, pinned, platformConfig, platformReady } = useTenant();
  const branding = tenant?.branding || null;
  const useBranded = pinned && !!tenant;
  const brandColor = (useBranded && branding?.primary_color) || IITH.saffron;
  const [tenantQuery, setTenantQuery] = useState('');
  const [staffIdMode, setStaffIdMode] = useState(false);

  const singleClinic = SINGLE_TENANT || platformConfig?.single_tenant_mode;
  const campusPortal = Boolean(useBranded || singleClinic || SINGLE_TENANT);
  // Campus hospital portal: always email → OTP (never Doctor ID / password).
  const forceOtpLogin = campusPortal && !staffIdMode;

  const auth = useLoginAuth({ slug, setUser, forceOtpLogin });
  const detectedRole = useMemo(
    () => (forceOtpLogin ? 'email' : (detectRole(auth.userId) || 'email')),
    [auth.userId, forceOtpLogin],
  );
  const hospitalName = (useBranded && (branding?.display_name || tenant?.name)) || IITH.hospital;
  // Tenant name lives on the brand panel / chrome — form card is "Sign in" only.
  const chrome = forceOtpLogin
    ? {
      ...HOSPITAL_PORTAL_CHROME,
      title: 'Sign in',
      subtitle: 'Staff & campus portal · email OTP',
    }
    : (ROLE_CHROME[detectedRole] || HOSPITAL_PORTAL_CHROME);

  useEffect(() => {
    if (slug || pinned) return;
    const urlSlug = (new URLSearchParams(window.location.search).get('tenant') || '').toLowerCase();
    if (urlSlug) setSlug(urlSlug);
  }, [slug, pinned, setSlug]);

  const filteredTenants = useMemo(() => {
    const seen = new Set();
    const out = [];
    for (const t of availableTenants) {
      if (seen.has(t.slug)) continue;
      seen.add(t.slug);
      out.push(t);
    }
    const q = tenantQuery.trim().toLowerCase();
    if (!q) return out;
    return out.filter((t) =>
      (t.name || '').toLowerCase().includes(q) || (t.slug || '').toLowerCase().includes(q));
  }, [availableTenants, tenantQuery]);

  const urlHasTenantParam = useMemo(() => {
    try { return !!new URLSearchParams(window.location.search).get('tenant'); }
    catch (_) { return false; }
  }, []);
  // Gate only when we truly have no clinic context (URL, pin, storage, or
  // platform default). Do not require ?tenant= in the address bar.
  if (platformReady && !singleClinic && !pinned && !urlHasTenantParam && !slug) {
    return <LoginTenantGate />;
  }

  const isStaff = detectedRole !== 'doctor' && detectedRole !== 'email';
  const isPasswordRequired = !forceOtpLogin && (!isStaff || ['pharmacy', 'storesadmin'].includes(detectedRole));

  return (
    <div
      className="min-h-[100dvh] flex flex-col lg:flex-row"
      style={{ background: IITH.navy }}
      data-testid="login-page"
    >
      <LoginBrandPanel
        name={hospitalName}
        logo={useBranded ? branding?.logo_url : null}
        primary={brandColor}
        tagline={(useBranded && branding?.tagline) || IITH.tagline}
        heroImage={useBranded ? branding?.hero_image_url : null}
      />
      <section
        className="relative flex-1 flex items-center justify-center px-4 sm:px-8 py-10 sm:py-14"
        style={{
          background:
            `radial-gradient(ellipse 60% 40% at 70% 0%, ${brandColor}22 0%, transparent 55%),` +
            ` linear-gradient(180deg, ${IITH.navyMid} 0%, ${IITH.ink} 100%)`,
        }}
      >
        <LoginFormPanel
          chrome={chrome}
          brandColor={brandColor}
          useBranded={campusPortal}
          singleClinic={singleClinic || campusPortal}
          error={auth.error}
          otpChallenge={auth.otpChallenge}
          loading={auth.loading}
          userId={auth.userId}
          setUserId={auth.setUserId}
          password={auth.password}
          setPassword={auth.setPassword}
          isStaff={isStaff}
          isPasswordRequired={isPasswordRequired}
          passwordPlaceholder="Password"
          isOtpLogin={forceOtpLogin}
          staffIdMode={staffIdMode}
          onToggleStaffMode={() => {
            setStaffIdMode((v) => !v);
            auth.setOtpChallenge(null);
            auth.setError('');
            auth.setUserId('');
            auth.setPassword('');
          }}
          handleSubmit={auth.handleSubmit}
          handleOtpVerify={auth.handleOtpVerify}
          onOtpBack={() => { auth.setOtpChallenge(null); auth.setError(''); }}
          slug={slug}
          setSlug={setSlug}
          tenant={tenant}
          availableTenants={availableTenants}
          filteredTenants={filteredTenants}
          tenantQuery={tenantQuery}
          setTenantQuery={setTenantQuery}
        />
      </section>
    </div>
  );
}
